<template>
  <div class="users-tab">
    <div class="cards-inline">
      <div class="card users-stats">
        <div class="stats-inline">
          <div class="stat">
            <div class="top">
              <div class="icon-container">
                <i class="bi bi-person-check" />
              </div>
              <span v-if="currentCompany.members">
                {{ currentCompany.members.length }}
              </span>
            </div>
            Utilisateur{{
              pluralize(currentCompany.members.length !== 1)
            }}
            ajouté{{ pluralize(currentCompany.members.length !== 1) }}
          </div>
          <div class="stat">
            <div class="top">
              <div class="icon-container">
                <i class="bi bi-person-add" />
              </div>
              <span
                v-if="currentCompany.members"
                :class="{ big: !currentCompany.max_members }"
              >
                {{
                  !currentCompany.max_members
                    ? "∞"
                    : currentCompany.max_members - currentCompany.members.length
                }}
              </span>
            </div>
            Place{{
              pluralize(
                !currentCompany.max_members ||
                  currentCompany.max_members - currentCompany.members.length !==
                    1
              )
            }}
            restante{{
              pluralize(
                !currentCompany.max_members ||
                  currentCompany.max_members - currentCompany.members.length !==
                    1
              )
            }}
          </div>
        </div>
        <Toast
          v-if="currentCompany.max_members"
          text="Besoin de plus d'utilisateurs ? <b style='cursor: pointer'>Contactez notre équipe en cliquant ici !</b>"
          color="yellow"
          icon="bi-info-circle"
          :closable="false"
          @click="$router.push({ name: 'contact' })"
        />
      </div>
      <div class="card add-user">
        <h4>Ajouter un utilisateur</h4>
        <VForm
          :button="{
            label: 'Ajouter le membre',
            class: 'primary full-size',
            hotDisabled: true,
          }"
          :externalRule="state.role !== null"
          :inline="true"
          :displayErrors="false"
          :callback="addNewMember"
          :clean="true"
        >
          <template #form-body>
            <VField
              label="Adresse e-mail*"
              name="add-user-email"
              placeholder="adresse@email.com"
              type="email"
              v-model="state.mail"
              rules="required|email|max:50"
              :disabled="!hasSlotAvailable"
              :displayError="true"
            />
            <div class="input-wrapper">
              <label for="add-user-role">
                Rôle de l'utilisateur
                <VTooltip placement="auto" :triggers="['hover']" theme="large">
                  <i class="bi bi-info-circle" />
                  <template #popper>
                    <p>
                      Le rôle
                      <span class="primary-color bold">administrateur</span> est
                      dédié à la gestion et à la consultation :
                    </p>
                    <ul>
                      <li>
                        Il donne accès à toutes les pages et aux informations
                        privées de l’organisation.
                      </li>
                      <li>Il a la possibilité de créditer le compte.</li>
                    </ul>
                    <br />
                    <p>
                      Le rôle
                      <span class="primary-color bold">utilisateur</span> est
                      dédié à la consultation :
                    </p>
                    <ul>
                      <li>
                        Il ne donne pas accès aux factures et à la listes des
                        utilisateurs de l’organisation.
                      </li>
                      <li>Il n’a pas la possibilité de créditer le compte.</li>
                    </ul>
                  </template>
                </VTooltip>
              </label>
              <v-select
                id="add-user-role"
                name="add-user-role"
                label="name"
                v-model="state.role"
                placeholder="Sélectionner le rôle"
                :searchable="false"
                :reduce="(role) => role.id"
                :clearable="false"
                :options="[
                  {
                    id: 1,
                    name: 'Administrateur',
                  },
                  {
                    id: 2,
                    name: 'Utilisateur',
                  },
                ]"
                :disabled="!hasSlotAvailable"
              />
            </div>
          </template>
        </VForm>
        <div
          class="premium-alert"
          v-if="!hasSlotAvailable && currentCompany.max_members"
        >
          <img
            src="@/assets/images/illustration/payment/icon-rocket.svg"
            alt="Premium rocket"
          />
          <p>
            Vous ne pouvez plus ajouter d’utilisateur. Pour agrandir votre
            équipe,
            <router-link class="bold" :to="{ name: 'contact' }"
              >contactez nous en cliquant ici !</router-link
            >
          </p>
        </div>
      </div>
    </div>
    <div class="card users-table">
      <h4>Liste des utilisateurs</h4>
      <p>
        Retrouvez tous les membres de votre équipe ainsi que leurs informations.
        Vous pouvez modifier leur rôle et les révoquer à tout moment.
      </p>
      <Table
        v-if="currentCompany.members"
        :data="currentCompany.members"
        :fields="state.fields"
      >
        <template #user="{ data }">
          <p v-if="!data.user" class="muted">En attente</p>
          <div v-else class="container" style="gap: 8px">
            <img
              class="picture"
              :src="
                data.user.image_id
                  ? getImage(data.user.image_id)
                  : getRandomUserPicture(data.user.first_name)
              "
            />
            <span>{{ data.user.first_name }} {{ data.user.last_name }}</span>
          </div>
        </template>
        <template #pending_email="emailProps">
          <div
            class="container"
            v-text="
              emailProps.data.user?.email || emailProps.data.pending_email
            "
          />
        </template>
        <template #creation_time="{ data }">
          <div
            :style="{ width: 'fit-content' }"
            v-text="getDate(data.creation_time, 'D MMM YYYY')"
            v-tooltip="{
              content: getDate(data.creation_time, 'D MMM YYYY à HH[h]mm'),
              placement: 'top',
            }"
          />
        </template>
        <template #status="statusProps">
          <div class="container">
            <Tag
              :color="statusProps.data.user ? 'green' : 'grey-light'"
              :text="`Invitation ${
                statusProps.data.user ? 'acceptée' : 'en attente'
              }`"
            />
          </div>
        </template>
        <template #role="roleProps">
          <div class="input-wrapper">
            <v-select
              id="role"
              name="role"
              label="name"
              v-model="roleProps.data.role"
              :reduce="(role) => role.id"
              :searchable="false"
              :clearable="false"
              :disabled="
                user.user_id === roleProps.data.user?.user_id ||
                roleProps.data.role === 0
              "
              :options="
                roleProps.data.role === 0
                  ? [
                      {
                        id: 0,
                        name: 'Propriétaire',
                      },
                    ]
                  : [
                      {
                        id: 1,
                        name: 'Administrateur',
                      },
                      {
                        id: 2,
                        name: 'Utilisateur',
                      },
                    ]
              "
              @update:modelValue="updateRole(roleProps.data)"
            />
          </div>
        </template>
        <template #actions="actionsProps">
          <Button
            :disabled="
              user.user_id === actionsProps.row.user?.user_id ||
              actionsProps.row.role === 0
            "
            :label="actionsProps.row.user ? 'Révoquer' : 'Annuler l\'ajout'"
            class="icon icon-left red outline"
            icon="bi-trash"
            @click="
              (state.showRevokeMember = true),
                (state.currentMember = actionsProps.row)
            "
          />
        </template>
      </Table>
    </div>
    <RevokeMember
      :isOpen="state.showRevokeMember"
      :member="state.currentMember"
      @confirm="revokeMember(state.currentMember)"
      @close="state.showRevokeMember = null"
    />
  </div>
</template>

<script setup>
import { reactive, computed } from "vue";
import { useCompanyStore } from "@/stores/company.js";
import { useUserStore } from "@/stores/user.js";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getDate, dateIsBefore, pluralize } from "@/composables/utils.js";
import { getError } from "@/composables/error.js";
import { getImage, getRandomUserPicture } from "@/composables/getImage.js";
import RevokeMember from "@/components/modals/app/RevokeMember.vue";

const route = useRoute();

const toast = useToast();

const { user } = useUserStore();

const {
  currentCompany,
  getCompanyMembers,
  addCompanyMember,
  updateCompanyMemberRole,
  revokeCompanyMembers,
} = useCompanyStore();

if (!currentCompany.members) {
  await getCompanyMembers(route.params.companyId);
}

const hasSlotAvailable = computed(() => {
  if (!currentCompany.members) return null;

  if (!currentCompany.max_members) return true;

  return currentCompany.max_members - currentCompany.members.length > 0;
});

const state = reactive({
  mail: "",
  role: null,
  currentMember: {},
  showRevokeMember: false,
  fields: [
    {
      id: "user",
      label: "Profil",
      sort(a, b, dir) {
        if (!a && !b) return -1;
        if (!a && b) return 1;
        if (a && !b) return -1;

        if (dir === "ASC") {
          return `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`
          );
        }

        return `${b.first_name} ${b.last_name}`.localeCompare(
          `${a.first_name} ${a.last_name}`
        );
      },
    },
    {
      id: "pending_email",
      label: "Adresse e-mail",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "creation_time",
      label: "Date d'ajout",
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "status",
      label: "Statut",
      defaultSortDir: "DESC",
      multipleSort: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          if (!a.user && b.user) return -1;
          if (a.user && !b.user) return 1;
        }

        if (a.user && !b.user) return -1;
        if (!a.user && b.user) return 1;
      },
    },
    {
      id: "role",
      label: "Rôle",
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") return b - a;
        return a - b;
      },
    },
  ],
});

async function addNewMember() {
  if (
    currentCompany.members.find((user) => user.pending_email === state.mail)
  ) {
    toast.error("Adresse e-mail déjà ajoutée.");
    document
      .querySelector("[name='add-user-email']")
      .parentNode.parentNode.classList.add("invalid");
    return;
  } else {
    document
      .querySelector("[name='add-user-email']")
      .parentNode.parentNode.classList.remove("invalid");
  }

  const resp = await addCompanyMember(route.params.companyId, {
    email: state.mail,
    role: state.role,
  });

  if (resp.pending_email) {
    resetForm();
    toast.success("Membre ajouté avec succès.");
  } else {
    toast.error(getError(resp.key));
  }
}

async function updateRole(member) {
  const { new_role } = await updateCompanyMemberRole(
    route.params.companyId,
    member.member_id,
    member.role
  );

  let toastOptions;

  if (new_role) {
    toastOptions = {
      label: "Modification de rôle effectuée",
      type: "success",
    };
  } else {
    toastOptions = {
      label: "Une erreur est survenue lors de la modification du rôle.",
      type: "error",
    };
  }

  toast(toastOptions.label, { type: toastOptions.type });
}

async function revokeMember(member) {
  const resp = await revokeCompanyMembers(
    route.params.companyId,
    member.member_id
  );

  let toastOptions;

  if (!resp.code) {
    toastOptions = {
      label: "Membre révoqué avec succès",
      type: "success",
    };
  } else {
    toastOptions = {
      label: "Une erreur est survenue lors de la révoquation du membre.",
      type: "error",
    };
  }

  state.showRevokeMember = false;

  toast(toastOptions.label, { type: toastOptions.type });
}

function resetForm() {
  state.mail = "";
  state.role = null;
  document
    .querySelector("[name='add-user-email']")
    .parentNode.parentNode.classList.remove("invalid");
}
</script>
